import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useDispatch } from 'react-redux';

import {
  assignWarrantyReplacement,
  previewWarrantyTicket
} from 'src/features/deviceSlice';

const useStyles = makeStyles(() => ({
  dialogPaper: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 10,
    overflow: 'hidden'
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    background: '#d9042f',
    color: '#fff'
  },
  dialogHeaderTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#fff'
  },
  closeButton: {
    color: '#fff',
    padding: 4
  },
  dialogContent: {
    padding: '16px',
    backgroundColor: '#ffffff'
  },
  fieldRow: {
    marginBottom: 12,
    '& .MuiOutlinedInput-root': {
      borderRadius: 6,
      backgroundColor: '#fff',
      fontSize: 13
    },
    '& .MuiOutlinedInput-input': {
      padding: '8.5px 12px'
    }
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#334155',
    marginBottom: 4
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: '#64748b',
    marginBottom: 6
  },
  panelGroup: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 8,
    margin: '12px 0',
    '@media (max-width: 500px)': {
      flexDirection: 'column'
    }
  },
  panel: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  panelArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8'
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    fontSize: 12,
    padding: '3px 0',
    '& > span:first-child': {
      color: '#64748b'
    },
    '& > span:last-child': {
      color: '#0f172a',
      fontWeight: 600,
      textAlign: 'right'
    }
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 6,
    backgroundColor: '#fdecea',
    border: '1px solid #f8d0d0',
    margin: '8px 0'
  },
  errorText: {
    fontSize: 12,
    color: '#991b1b',
    lineHeight: 1.4
  },
  targetBox: {
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    margin: '10px 0',
    fontSize: 12,
    color: '#0369a1'
  },
  dialogActions: {
    padding: '10px 16px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e2e8f0',
    gap: 8
  },
  primaryActionButton: {
    minWidth: 120,
    height: 36,
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'none',
    boxShadow: 'none',
    background: '#d9042f',
    color: '#fff',
    padding: '6px 16px',
    '&:hover': {
      background: '#b80327'
    },
    '&.Mui-disabled': {
      background: '#f2b9c4',
      color: '#fff'
    }
  },
  secondaryActionButton: {
    minWidth: 70,
    height: 36,
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'none',
    background: 'none',
    border: '1px solid #cbd5e1',
    color: '#475569',
    padding: '6px 16px',
    '&:hover': {
      background: '#f8fafc',
      borderColor: '#94a3b8'
    }
  }
}));

export default function SwapSerialModal({ open, onClose, onSuccess, ticket }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const faultySerial = ticket?.faulty_serial || ticket?.serial_faulty || '';
  const [replacementSerial, setReplacementSerial] = useState('');
  const [note, setNote] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExecute, setLoadingExecute] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const isBusy = loadingPreview || loadingExecute;

  useEffect(() => {
    if (!open) return;
    setReplacementSerial(ticket?.replacement_serial || ticket?.serial_replacement || '');
    setNote('');
    setPreview(null);
    setError('');
    setLoadingPreview(false);
    setLoadingExecute(false);
  }, [open, ticket]);

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const handlePreview = async () => {
    if (!faultySerial || !replacementSerial.trim()) {
      setError('Vui lòng nhập Serial thiết bị bảo hành');
      return;
    }
    if (faultySerial.trim() === replacementSerial.trim()) {
      setError('Serial bảo hành phải khác Serial lỗi');
      return;
    }
    setError('');
    setPreview(null);
    setLoadingPreview(true);
    try {
      const res = await dispatch(
        previewWarrantyTicket({
          faulty_serial: faultySerial.trim(),
          replacement_serial: replacementSerial.trim()
        })
      ).unwrap();
      const payload = res?.payload || res;
      setPreview(payload);
    } catch (err) {
      const msg = typeof err === 'string'
        ? err
        : err?.message || 'Không thể kiểm tra Serial bảo hành. Vui lòng kiểm tra lại.';
      setError(msg);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExecute = async () => {
    if (!preview?.can_swap) return;
    setError('');
    setLoadingExecute(true);
    try {
      const res = await dispatch(
        assignWarrantyReplacement({
          id: ticket?.id,
          replacement_serial: replacementSerial.trim(),
          confirm: true
        })
      ).unwrap();
      const payload = res?.payload || res;
      onSuccess && onSuccess(payload);
      onClose && onClose();
    } catch (err) {
      const msg = typeof err === 'string'
        ? err
        : err?.message || 'Gán thiết bị bảo hành thất bại. Vui lòng thử lại.';
      setError(msg);
    } finally {
      setLoadingExecute(false);
    }
  };

  const renderDevicePanel = (panelTitle, item) => (
    <Box className={classes.panel}>
      <Typography className={classes.sectionLabel}>{panelTitle}</Typography>
      {item ? (
        <>
          <Box className={classes.row}>
            <span>Serial</span>
            <span>{item.serial}</span>
          </Box>
          <Box className={classes.row}>
            <span>Trạng thái</span>
            <span>{item.status}</span>
          </Box>
          <Box className={classes.row}>
            <span>Loại thiết bị</span>
            <span>{item.device_type_name || '--'}</span>
          </Box>
          <Box className={classes.row}>
            <span>Biển số</span>
            <span>{item.license_plate || '--'}</span>
          </Box>
          <Box className={classes.row}>
            <span>Đại lý / Kho</span>
            <span>{item.agency_name || '--'}</span>
          </Box>
        </>
      ) : (
        <Typography className={classes.errorText}>Không tìm thấy thông tin</Typography>
      )}
    </Box>
  );

  const renderError = content => (
    <Box className={classes.errorBox}>
      <WarningIcon fontSize="small" style={{ color: '#d9042f', flexShrink: 0, marginTop: 1 }} />
      <Box className={classes.errorText}>{content}</Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.dialogHeader}>
        <Typography className={classes.dialogHeaderTitle}>
          Đổi thiết bị bảo hành (Mã phiếu: {ticket?.code || ticket?.ticket_code || ticket?.id || '--'})
        </Typography>
        <IconButton
          size="small"
          className={classes.closeButton}
          onClick={handleClose}
          disabled={isBusy}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Serial thiết bị lỗi</Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={faultySerial}
            disabled
          />
        </Box>

        <Box className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Serial thiết bị bảo hành *</Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Nhập serial thiết bị bảo hành trong kho..."
            value={replacementSerial}
            onChange={e => {
              setReplacementSerial(e.target.value);
              setPreview(null);
            }}
            disabled={isBusy}
          />
        </Box>

        <Box className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Ghi chú đổi thiết bị</Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Nhập ghi chú (nếu có)..."
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={isBusy}
            multiline
            rows={2}
          />
        </Box>

        {error && renderError(error)}

        {preview && (
          <>
            <Box className={classes.panelGroup}>
              {renderDevicePanel('Thiết bị lỗi', preview.faulty)}
              <Box className={classes.panelArrow}>
                <ArrowForwardIcon fontSize="small" />
              </Box>
              {renderDevicePanel('Thiết bị bảo hành', preview.replacement)}
            </Box>

            {!preview.can_swap &&
              renderError(
                <>
                  <strong>Không thể đổi thiết bị</strong>
                  {(preview.issues || []).map((issue, idx) => (
                    <div key={idx}>• {issue}</div>
                  ))}
                </>
              )}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.secondaryActionButton}
          onClick={handleClose}
          disabled={isBusy}
        >
          Hủy
        </Button>
        {preview?.can_swap ? (
          <Button
            className={classes.primaryActionButton}
            onClick={handleExecute}
            disabled={isBusy}
            startIcon={loadingExecute ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Xác nhận Swap
          </Button>
        ) : (
          <Button
            className={classes.primaryActionButton}
            onClick={handlePreview}
            disabled={isBusy}
            startIcon={loadingPreview ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Kiểm tra & Swap
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
